import React, { useRef, useState } from 'react';
import { Button, Icon } from '../../components/ui';
import './NewCandidatesPanel.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ALLOWED = ['pdf', 'doc', 'docx'];
const MAX_BYTES = 10 * 1024 * 1024;

export const emptyRow = () => ({ key: Math.random().toString(36).slice(2), name: '', email: '', phone: '', file: null });

const ext = (f) => (f.name.split('.').pop() || '').toLowerCase();

/** A typed row counts once a name or email is entered; it is valid when both are present and the email looks right. */
export function rowState(r) {
  const touched = !!(r.name.trim() || r.email.trim() || r.phone.trim() || r.file);
  if (!touched) return 'empty';
  if (!r.name.trim()) return 'Name is required';
  if (!EMAIL_RE.test(r.email.trim())) return 'Enter a valid email';
  return 'ok';
}

/**
 * "New candidate" tab of Add candidates: type people in (name, email, phone, optional résumé)
 * or drop résumés in bulk (name and email read from each file). Everything stays in the dialog;
 * the parent creates them on submit.
 */
export default function NewCandidatesPanel({ mode, setMode, rows, setRows, files, setFiles, examSetName, disabled }) {
  const dropRef = useRef(null);
  const pickRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState([]);

  const update = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const remove = (key) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : [emptyRow()]));

  const addFiles = (list) => {
    const bad = [];
    const good = [];
    Array.from(list || []).forEach((f) => {
      if (!ALLOWED.includes(ext(f))) bad.push(`${f.name}: only PDF or Word files`);
      else if (f.size > MAX_BYTES) bad.push(`${f.name}: larger than 10 MB`);
      else good.push(f);
    });
    setRejected(bad);
    setFiles((cur) => {
      const seen = new Set(cur.map((f) => `${f.name}:${f.size}`));
      return [...cur, ...good.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="wz-ncp-seg" role="tablist" aria-label="How to add new candidates">
        <button type="button" role="tab" aria-selected={mode === 'manual'} className={mode === 'manual' ? 'on' : ''} onClick={() => setMode('manual')}>
          Enter details
        </button>
        <button type="button" role="tab" aria-selected={mode === 'upload'} className={mode === 'upload' ? 'on' : ''} onClick={() => setMode('upload')}>
          Upload résumés
        </button>
      </div>

      {mode === 'manual' && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 300, overflowY: 'auto' }}>
            {rows.map((r, i) => {
              const st = rowState(r);
              const err = st !== 'ok' && st !== 'empty' ? st : '';
              return (
                <div key={r.key} className="wz-newrow">
                  <div className="wz-newrow-grid">
                    <input aria-label={`Name ${i + 1}`} placeholder="Full name *" value={r.name} disabled={disabled}
                      onChange={(e) => update(r.key, { name: e.target.value })} />
                    <input aria-label={`Email ${i + 1}`} type="email" placeholder="Email *" value={r.email} disabled={disabled}
                      onChange={(e) => update(r.key, { email: e.target.value })} />
                    <input aria-label={`Phone ${i + 1}`} placeholder="Phone" value={r.phone} disabled={disabled}
                      onChange={(e) => update(r.key, { phone: e.target.value })} />
                    <label className="wz-filebtn" title={r.file ? r.file.name : 'Attach résumé (optional)'}>
                      <Icon name="file" />
                      <span className="wz-ellipsis">{r.file ? r.file.name : 'Résumé'}</span>
                      <input type="file" accept=".pdf,.doc,.docx" hidden disabled={disabled}
                        onChange={(e) => update(r.key, { file: e.target.files?.[0] || null })} />
                    </label>
                    <Button iconOnly small icon="trash" aria-label={`Remove row ${i + 1}`} disabled={disabled} onClick={() => remove(r.key)} />
                  </div>
                  {err && <span className="wz-newrow-err">{err}</span>}
                </div>
              );
            })}
          </div>
          <Button variant="dashed" small icon="plus" disabled={disabled} onClick={() => setRows((rs) => [...rs, emptyRow()])}>
            Add another person
          </Button>
        </>
      )}

      {mode === 'upload' && (
        <>
          <div
            ref={dropRef}
            className={`wz-drop${dragging ? ' over' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => !disabled && pickRef.current?.click()}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !disabled) { e.preventDefault(); pickRef.current?.click(); } }}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); if (!disabled) addFiles(e.dataTransfer.files); }}
          >
            <Icon name="download" />
            <b>Drop résumés here or click to choose</b>
            <span className="wz-sub">PDF or Word, up to 10 MB each. Name and email are read from each résumé.</span>
            <input ref={pickRef} type="file" accept=".pdf,.doc,.docx" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          </div>
          {rejected.length > 0 && (
            <div className="wz-error" role="alert">{rejected.map((m) => <div key={m}>{m}</div>)}</div>
          )}
          {files.length > 0 && (
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {files.map((f) => (
                <div key={`${f.name}:${f.size}`} className="wz-filerow">
                  <Icon name="file" />
                  <span className="wz-ellipsis" style={{ flex: 1 }}>{f.name}</span>
                  <span className="wz-sub" style={{ fontSize: 12 }}>{Math.max(1, Math.round(f.size / 1024))} KB</span>
                  <Button iconOnly small icon="x" aria-label={`Remove ${f.name}`} disabled={disabled}
                    onClick={() => setFiles((cur) => cur.filter((x) => x !== f))} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
